import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
  notes: string | null
  activity_type: string | null
  activity_date: string | null
  end_date: string | null
  manager: string | null
  added_by: string | null
  send_reminder: boolean
  is_hpi_coordinated: boolean
  status: number
  employees_count: number
  list_items_count: number
  documents_count: number
  time_entries_count: number
  expenses_count: number
  todos_count: number
}

interface Props {
  activity: Activity
  can_delete: boolean
}

export default function Show({ activity, can_delete }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={activity.title} />
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{activity.title}</h1>
          <div className="flex gap-2">
            <Link
              href={`/marketing_activities/${activity.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {can_delete && (
              <button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this event?")) {
                    router.delete(`/marketing_activities/${activity.id}`)
                  }
                }}
                className="bg-hpi-red rounded px-4 py-2 text-white hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-4">
          <div>
            <dt className="font-semibold">Type</dt>
            <dd>{activity.activity_type}</dd>
          </div>
          <div>
            <dt className="font-semibold">Date</dt>
            <dd>
              {formatDate(activity.activity_date, {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
              {activity.end_date && (
                <>
                  {" \u2013 "}
                  {formatDate(activity.end_date, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </>
              )}
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Team Leader</dt>
            <dd>{activity.manager}</dd>
          </div>
          <div>
            <dt className="font-semibold">Added By</dt>
            <dd>{activity.added_by}</dd>
          </div>
          <div className="col-span-2">
            <dt className="font-semibold">Notes</dt>
            <dd>{activity.notes}</dd>
          </div>
        </dl>
        <div className="mt-6 flex gap-4">
          <Link
            href={`/marketing_activities/${activity.id}/employees`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Employees ({activity.employees_count})
          </Link>
          <Link
            href={`/marketing_activities/${activity.id}/list_items`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Attendees ({activity.list_items_count})
          </Link>
          <Link
            href={`/marketing_activities/${activity.id}/documents`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Documents ({activity.documents_count})
          </Link>
          <Link
            href={`/marketing_activities/${activity.id}/time_entries`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Time Entries ({activity.time_entries_count})
          </Link>
          <Link
            href={`/marketing_activities/${activity.id}/expenses`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Expenses ({activity.expenses_count})
          </Link>
          <Link
            href={`/marketing_todos?marketing_activity_id=${activity.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Tasks ({activity.todos_count})
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
